import React, { useMemo, useState, useEffect } from 'react';
import { isSafeFetchUrl, openExternalUrl } from '@/utils/url-utils';
import { Button } from '@/components/ui/button';
import { Download, ExternalLink, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { FileIconTile } from '@/pages/workflow/component/file-icon-tile';
import {
  getFileIcon,
  getFileIconBackgroundColor,
} from '@/constants/node-styling';
import { CustomNode, NodeFile, NodeInputType } from '@/types/node-types';
import {
  downloadFileWithName,
  refreshFileUrls,
} from '@/services/workflow/get-file';
import { useOrgStore } from '@/stores/org-store';
import { useNotification } from '@/components/notifications';
import * as Sentry from '@sentry/react';
import { getCleanMarkdownData } from '@/pages/workflow/helper/markdown-utils';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
  officeEmbedUrl,
  OFFICE_EXTENSIONS,
} from '@/pages/workflow/component/display-components';

const FILE_TYPE_LABELS: Record<string, string> = {
  png: 'PNG FILE',
  jpg: 'JPEG FILE',
  jpeg: 'JPEG FILE',
  gif: 'GIF FILE',
  svg: 'SVG FILE',
  webp: 'WEBP FILE',
  doc: 'MICROSOFT WORD',
  docx: 'MICROSOFT WORD',
  xls: 'MICROSOFT EXCEL',
  xlsx: 'MICROSOFT EXCEL',
  csv: 'CSV FILE',
  ppt: 'MICROSOFT POWERPOINT',
  pptx: 'MICROSOFT POWERPOINT',
  pdf: 'PDF DOCUMENT',
  md: 'MARKDOWN',
  txt: 'TEXT FILE',
  json: 'JSON FILE',
  py: 'PYTHON FILE',
  js: 'JAVASCRIPT FILE',
  ts: 'TYPESCRIPT FILE',
  xml: 'XML FILE',
  html: 'HTML FILE',
  sql: 'SQL FILE',
  zip: 'ZIP ARCHIVE',
  parquet: 'PARQUET FILE',
};

function getFileTypeLabel(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return FILE_TYPE_LABELS[ext] || `${ext.toUpperCase()} FILE`;
}

interface FileWithNode {
  file: NodeFile;
  nodeId: string;
  nodeName: string;
  isMarkdownOutput?: boolean; // Flag for markdown result files
  markdownContent?: string; // Markdown content for download
  isTextOutput?: boolean; // Flag for text result files
  textContent?: string; // Text content for download
}

interface FilesTabContentProps {
  nodes: CustomNode[];
  /** When set, scopes the list to a single node (node context) and shows a back button. */
  selectedNodeId?: string | null;
  /** Called from the "All Files" back button to clear node selection. */
  workflowName?: string;
}

export const FilesTabContent: React.FC<FilesTabContentProps> = ({
  nodes,
  selectedNodeId = null,
  workflowName,
}) => {
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set(),
  );
  const [isRefreshingUrls, setIsRefreshingUrls] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [refreshedFilesMap, setRefreshedFilesMap] = useState<
    Map<string, NodeFile>
  >(new Map());
  const { currentOrg } = useOrgStore();
  const notification = useNotification();

  // Extract files from all nodes (only from node results, not parameters)
  const allFilesWithNodes = useMemo<FileWithNode[]>(() => {
    const files: FileWithNode[] = [];

    nodes.forEach((node) => {
      const struct = node.data.struct;
      const nodeName =
        struct.display_name || struct.user_config_name || 'Untitled Node';

      // Extract files from node results only (execution output files)
      if (node.data.result && typeof node.data.result === 'object') {
        const result = node.data.result as {
          files?: NodeFile[];
          display_modal?: NodeInputType;
          value?: unknown;
        };

        // Add regular files
        if (result.files && Array.isArray(result.files)) {
          result.files.forEach((file: NodeFile) => {
            files.push({
              file,
              nodeId: node.id,
              nodeName,
            });
          });
        }

        // Add markdown output as a virtual file.
        // Skipped when a real S3-backed file is already present in result.files
        // (large markdown nodes now upload to S3; the real file covers it) or
        // when value is null/undefined (was WS-nulled because a file ref exists).
        // TODO: Check with backend - display_modal for file_input nodes incorrectly returns MARKDOWN
        // Exclude file_input nodes even if they have display_modal === MARKDOWN
        if (
          result.display_modal === NodeInputType.MARKDOWN &&
          result.value != null &&
          (!result.files || result.files.length === 0) &&
          struct.node_sub_type !== 'file_input'
        ) {
          files.push({
            file: {
              name: `${nodeName} Output.md`,
              mime_type: 'text/markdown',
              s3_key: `${node.id}-markdown-output`,
              s3_bucket: '',
              s3_region: '',
              presigned_url: '',
              generated: true,
            },
            nodeId: node.id,
            nodeName,
            isMarkdownOutput: true,
            markdownContent: String(result.value),
          });
        }

        // Add text output as a virtual file.
        // Same guard: skip when real files exist or value is absent.
        if (
          result.display_modal === NodeInputType.TEXT_OUTPUT &&
          result.value != null &&
          (!result.files || result.files.length === 0) &&
          struct.node_sub_type !== 'file_input'
        ) {
          // Handle both string and JSON values
          let textContent: string;
          if (typeof result.value === 'string') {
            textContent = result.value;
          } else {
            // Convert JSON to formatted string
            textContent = JSON.stringify(result.value, null, 2);
          }

          files.push({
            file: {
              name: `${nodeName} Output.txt`,
              mime_type: 'text/plain',
              s3_key: `${node.id}-text-output`,
              s3_bucket: '',
              s3_region: '',
              presigned_url: '',
              generated: true,
            },
            nodeId: node.id,
            nodeName,
            isTextOutput: true,
            textContent,
          });
        }

        // Add audio output as a file. Unlike markdown/text, the value is
        // already a URL to the generated file, so it downloads like a
        // regular file rather than needing a blob built from inline content.
        // Skip when `files` already carries the real generated file (backend
        // nodes that populate both) to avoid listing the same audio twice.
        if (
          (!result.files || result.files.length === 0) &&
          result.display_modal === NodeInputType.AUDIO &&
          typeof result.value === 'string' &&
          result.value
        ) {
          files.push({
            file: {
              name: `${nodeName} Output.mp3`,
              mime_type: 'audio/mpeg',
              s3_key: `${node.id}-audio-output`,
              s3_bucket: '',
              s3_region: '',
              presigned_url: result.value,
              generated: true,
            },
            nodeId: node.id,
            nodeName,
          });
        }
      }
    });

    return files;
  }, [nodes]);

  // Refresh presigned URLs when files change or component mounts
  useEffect(() => {
    const refreshUrls = async () => {
      if (allFilesWithNodes.length === 0) {
        setRefreshedFilesMap(new Map());
        return;
      }

      if (!currentOrg?.orgid) {
        // No org, clear any stale refreshed URLs
        setRefreshedFilesMap(new Map());
        setIsRefreshingUrls(false);
        return;
      }

      setIsRefreshingUrls(true);
      try {
        // Extract all unique files (by s3_key) to refresh
        // Filter out virtual files (markdown/text outputs) that have no real S3 location
        const filesToRefresh = Array.from(
          new Map(
            allFilesWithNodes
              .filter((f) => f.file.s3_bucket && f.file.s3_key)
              .map((f) => [f.file.s3_key, f.file]),
          ).values(),
        );

        // Skip refresh if no real files to refresh
        if (filesToRefresh.length === 0) {
          setIsRefreshingUrls(false);
          return;
        }

        const freshFiles = await refreshFileUrls(
          currentOrg.orgid,
          filesToRefresh,
        );

        // Create a map of s3_key -> refreshed file for quick lookup
        const refreshedMap = new Map<string, NodeFile>();
        freshFiles.forEach((file) => {
          refreshedMap.set(file.s3_key, file);
        });

        setRefreshedFilesMap(refreshedMap);
      } catch (error) {
        console.error('Failed to refresh file URLs:', error);
        Sentry.captureException(error, {
          extra: { context: 'refresh-presigned-urls-tab' },
        });
      } finally {
        setIsRefreshingUrls(false);
      }
    };

    refreshUrls();
  }, [allFilesWithNodes, currentOrg?.orgid]);

  // Use refreshed files if available, otherwise use original files
  const filesWithRefreshedUrls = useMemo<FileWithNode[]>(() => {
    return allFilesWithNodes.map((fileWithNode) => {
      const refreshedFile = refreshedFilesMap.get(fileWithNode.file.s3_key);
      return {
        ...fileWithNode,
        file: refreshedFile || fileWithNode.file,
      };
    });
  }, [allFilesWithNodes, refreshedFilesMap]);

  // Scope to the selected node when in node context, otherwise show all files
  const filteredFiles = useMemo(() => {
    if (!selectedNodeId) {
      return filesWithRefreshedUrls;
    }
    return filesWithRefreshedUrls.filter((f) => f.nodeId === selectedNodeId);
  }, [filesWithRefreshedUrls, selectedNodeId]);

  // Group files by node
  const filesByNode = useMemo(() => {
    const grouped = new Map<string, FileWithNode[]>();
    filteredFiles.forEach((fileWithNode) => {
      const key = fileWithNode.nodeId;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(fileWithNode);
    });

    return grouped;
  }, [filteredFiles]);

  const handleDownloadFile = async (fileWithNode: FileWithNode) => {
    const fileKey = fileWithNode.file.id || fileWithNode.file.s3_key;
    setDownloadingFiles((prev) => new Set(prev).add(fileKey));

    try {
      // Handle markdown output files (including empty markdown)
      if (
        fileWithNode.isMarkdownOutput &&
        fileWithNode.markdownContent !== undefined
      ) {
        const cleanData = getCleanMarkdownData(fileWithNode.markdownContent);
        const blob = new Blob([cleanData], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileWithNode.file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      // Handle text output files (including empty text)
      else if (
        fileWithNode.isTextOutput &&
        fileWithNode.textContent !== undefined
      ) {
        const blob = new Blob([fileWithNode.textContent], {
          type: 'text/plain',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileWithNode.file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      // Regular file download
      else {
        const downloadUrl =
          fileWithNode.file.download_url || fileWithNode.file.presigned_url;
        await downloadFileWithName(downloadUrl, fileWithNode.file.name);
      }
    } catch (err) {
      Sentry.captureException(err, {
        extra: { context: 'file-download', fileName: fileWithNode.file.name },
      });
      throw err;
    } finally {
      setDownloadingFiles((prev) => {
        const next = new Set(prev);
        next.delete(fileKey);
        return next;
      });
    }
  };

  const handleDownloadAll = async () => {
    if (filteredFiles.length === 0 || isDownloadingAll) {
      return;
    }

    setIsDownloadingAll(true);
    const zip = new JSZip();
    let failureCount = 0;
    const usedNames = new Set<string>();

    const dedupeName = (name: string): string => {
      if (!usedNames.has(name)) {
        usedNames.add(name);
        return name;
      }
      const dot = name.lastIndexOf('.');
      const base = dot > 0 ? name.slice(0, dot) : name;
      const ext = dot > 0 ? name.slice(dot) : '';
      let i = 2;
      while (usedNames.has(`${base} (${i})${ext}`)) i++;
      const deduped = `${base} (${i})${ext}`;
      usedNames.add(deduped);
      return deduped;
    };

    for (const fileWithNode of filteredFiles) {
      const fileName = dedupeName(fileWithNode.file.name);
      try {
        if (
          fileWithNode.isMarkdownOutput &&
          fileWithNode.markdownContent !== undefined
        ) {
          zip.file(
            fileName,
            getCleanMarkdownData(fileWithNode.markdownContent),
          );
        } else if (
          fileWithNode.isTextOutput &&
          fileWithNode.textContent !== undefined
        ) {
          zip.file(fileName, fileWithNode.textContent);
        } else {
          const url =
            fileWithNode.file.download_url || fileWithNode.file.presigned_url;
          if (!isSafeFetchUrl(url)) {
            throw new Error('Refusing to fetch an unsafe file URL');
          }
          const resp = await fetch(url);
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          zip.file(fileName, await resp.blob());
        }
      } catch (err) {
        failureCount++;
        Sentry.captureException(err, {
          extra: {
            context: 'zip-download',
            fileName: fileWithNode.file.name,
          },
        });
      }
    }

    try {
      const blob = await zip.generateAsync({ type: 'blob' });
      const zipName = workflowName
        ? `${workflowName.replace(/[/\\?%*:|"<>]/g, '_')}.zip`
        : 'files.zip';
      saveAs(blob, zipName);
    } catch (err) {
      Sentry.captureException(err, {
        extra: { context: 'zip-generate' },
      });
      notification.error('Failed to create zip file');
      setIsDownloadingAll(false);
      return;
    }

    setIsDownloadingAll(false);

    if (failureCount === 0) {
      notification.success(`Downloaded ${filteredFiles.length} file(s) as zip`);
    } else {
      notification.error(
        `Zip created with ${filteredFiles.length - failureCount} file(s), ${failureCount} failed`,
      );
    }
  };

  const isFileDownloading = (fileWithNode: FileWithNode) => {
    const fileKey = fileWithNode.file.id || fileWithNode.file.s3_key;
    return downloadingFiles.has(fileKey);
  };

  const handlePreviewFile = (fileWithNode: FileWithNode) => {
    const ext = fileWithNode.file.name.split('.').pop()?.toLowerCase();
    if (ext && OFFICE_EXTENSIONS.includes(ext)) {
      const previewUrl =
        fileWithNode.file.proxy_url || fileWithNode.file.presigned_url;
      openExternalUrl(officeEmbedUrl(previewUrl));
    } else {
      openExternalUrl(fileWithNode.file.presigned_url);
    }
  };

  const fileCount = filteredFiles.length;
  const countLabel = `${fileCount} ${fileCount === 1 ? 'File' : 'Files'}`;

  const downloadAllButton = (
    <Button
      variant='ghost'
      size='sm'
      onClick={handleDownloadAll}
      disabled={fileCount === 0 || isDownloadingAll}
      className='gap-2'
    >
      <span>Download All</span>
      {isDownloadingAll ? (
        <Loader2 className='size-4 animate-spin' />
      ) : (
        <Download className='size-4' />
      )}
    </Button>
  );

  const nodeGroups = Array.from(filesByNode.entries());

  return (
    <div className='flex-1 flex flex-col min-h-0 overflow-hidden'>
      <div className='flex items-center justify-between pl-5 pr-1.5 py-1.5 shrink-0'>
        <span className='text-sm font-medium text-foreground'>
          {countLabel}
        </span>
        {downloadAllButton}
      </div>
      <Separator />

      <div className='flex-1 overflow-y-auto'>
        {isRefreshingUrls ? (
          <div className='flex items-center justify-center h-full gap-2 text-sm text-muted-foreground'>
            <Loader2 className='size-4 animate-spin' />
            <span>Loading files...</span>
          </div>
        ) : nodeGroups.length === 0 ? (
          <div className='flex items-center justify-center h-full text-sm text-muted-foreground'>
            No files available
          </div>
        ) : (
          nodeGroups.map(([nodeId, files], groupIndex) => {
            const nodeName = files[0]?.nodeName || 'Unknown Node';
            return (
              <React.Fragment key={nodeId}>
                {groupIndex > 0 && <Separator />}
                <div className='flex flex-col gap-2 p-4'>
                  {!selectedNodeId && (
                    <div className='px-0.5 pb-0.5'>
                      <span className='text-sm font-medium text-foreground'>
                        {nodeName}
                      </span>
                    </div>
                  )}
                  {files.map((fileWithNode, index) => {
                    const color = getFileIconBackgroundColor(
                      fileWithNode.file.name,
                    );
                    const isVirtual =
                      fileWithNode.isMarkdownOutput ||
                      fileWithNode.isTextOutput;
                    return (
                      <div
                        key={`${fileWithNode.file.s3_key}-${index}`}
                        className='rounded-lg bg-accent'
                      >
                        <div className='flex items-center gap-2 px-2.5 py-2'>
                          <FileIconTile
                            icon={getFileIcon(fileWithNode.file.name)}
                            backgroundColor={color}
                            size='small'
                            iconColor='#ffffff'
                            className='!size-7 !rounded-md shadow-sm'
                            iconClassName='size-4'
                          />
                          <div className='flex-1 min-w-0'>
                            <span
                              className='text-[10px] font-semibold tracking-wide block leading-tight'
                              style={{ color }}
                            >
                              {getFileTypeLabel(fileWithNode.file.name)}
                            </span>
                            <span className='text-sm font-medium text-foreground truncate block'>
                              {fileWithNode.file.name}
                            </span>
                          </div>
                          <div className='flex items-center shrink-0'>
                            {!isVirtual && (
                              <Button
                                variant='ghost'
                                size='sm'
                                onClick={() => handlePreviewFile(fileWithNode)}
                                title='Open'
                                className='size-7 p-0'
                              >
                                <ExternalLink className='size-3.5' />
                              </Button>
                            )}
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => {
                                handleDownloadFile(fileWithNode).catch(() => {
                                  notification.error(
                                    `Failed to download ${fileWithNode.file.name}`,
                                  );
                                });
                              }}
                              disabled={isFileDownloading(fileWithNode)}
                              title='Download'
                              className='size-7 p-0'
                            >
                              {isFileDownloading(fileWithNode) ? (
                                <Loader2 className='size-3.5 animate-spin' />
                              ) : (
                                <Download className='size-3.5' />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </React.Fragment>
            );
          })
        )}
      </div>
    </div>
  );
};
